import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { Trash2 as DeleteIcon, Download as DownloadIcon, PenSquareIcon as EditIcon, X, Power, Type } from 'lucide-react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Box from '@mui/material/Box';
import { useForm, Controller } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../../axios/api';
import PageHeader from '../../componentsII/PageHeader';
import { FormHelperText } from '@mui/material';
import CustomButton from '../../componentsII/Buttons';
import dayjs from 'dayjs';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CloudinaryFileUploader from '../../components/Common/CloudinaryFileUploader';
import { showNotification } from '../../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';
import { TextField, TableFooter, TablePagination } from '@mui/material';
import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';

// Custom CSS for the DatePicker
const datePickerStyles = `
  .react-datepicker {
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-size: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .react-datepicker__month-container {
    float: none;
    width: 280px;
  }
  
  .react-datepicker__header {
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;
    padding: 1rem;
  }
  
  .react-datepicker__month-wrapper {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
    padding: 0.5rem;
  }
  
  .react-datepicker__month-text {
    margin: 0.5rem;
    padding: 0.5rem;
    width: auto !important;
    border-radius: 4px;
  }
  
  .react-datepicker__month-text:hover {
    background-color: #f0f0f0;
  }
  
  .react-datepicker__month-text--selected {
    background-color: #1976d2 !important;
    color: white !important;
  }
  
  .react-datepicker__year-wrapper {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    padding: 0.5rem;
    max-width: none;
  }
  
  .react-datepicker__year-text {
    padding: 0.5rem;
    width: auto !important;
    border-radius: 4px;
  }
  
  .react-datepicker__year-text:hover {
    background-color: #f0f0f0;
  }
  
  .react-datepicker__year-text--selected {
    background-color: #1976d2 !important;
    color: white !important;
  }
  
  .date-picker-input {
    width: 100%;
    padding: 0.50rem;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 1rem;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s ease;
  }
  
  .date-picker-input:hover {
    border-color: #1976d2;
  }
  
  .date-picker-input:focus {
    border-color: #1976d2;
    outline: none;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }

  .react-datepicker-popper {
    z-index: 9999 !important;
  }
`;

const fileSchema = z.object({
  name: z.string(),
  path: z.string(),
  size: z.number(),
  filename: z.string()
}).nullable();

const formSchema = z.object({
  sourceId: z.number().min(0, "Please select a source"),
  template: fileSchema,
  startDate: z.date({ required_error: "Please select a start date" }),
  endDate: z.date({ required_error: "Please select an end date" }),
  templateFor: z.string().min(1, "Please select a template for")
});


const Scope3TemplateMaster = () => {
  const [sources, setSources] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState([]);
  const dispatch = useDispatch();

  const [totalScope3Templates, setTotalScope3Templates] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL', // for is_active field
    type: 'ALL', // for template type
    sortBy: 'created_at',
    sortOrder: 'desc',
    categoryId: 'ALL' // for filtering by category
  });


  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = datePickerStyles;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  const { control, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sourceId: "",
      template: [],
      startDate: null,
      endDate: null,
      templateFor: ""
    }
  });


  const getSources = async () => {
    try {
      const res = await api.get(`/common/scope3Sources`);
      setSources(res.data.data);
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  };

  const getTemplates = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const res = await api.get(`admin/getScope3Templates`, {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          isActive: filterParams.status === 'ALL' ? '' : filterParams.status === 'ACTIVE' ? 1 : 0,
          type: filterParams.type === 'ALL' ? '' : filterParams.type.toLowerCase(),
          catId: filterParams.categoryId === 'ALL' ? '' : filterParams.categoryId,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder
        }
      });
      setTemplates(res.data.data);
      setTotalScope3Templates(res.data.pagination.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch templates',
        type: 'error',
      }));
    }
  };

  useEffect(() => {
    getSources();
    getTemplates();
  }, [page, rowsPerPage, filters]);

  const debouncedSearch = debounce((value) => { setFilters(prev => ({ ...prev, search: value })); setPage(0); }, 500);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      formData.append('startDate', dayjs(data.startDate).format('YYYY-MM-DD'));
      formData.append('endDate', dayjs(data.endDate).format('YYYY-MM-DD'));
      formData.append('id', selectedData?.id || 0);
      formData.append('cat_id', data.sourceId);

      formData.append('other', data.templateFor === 'other' ? 1 : 0);
      formData.append('amount_based', data.templateFor === 'amount_based' ? 1 : 0);
      formData.append('quantity_based', data.templateFor === 'quantity_based' ? 1 : 0);

      if (data.template) {
        formData.append('templatePath', data.template.path);
        formData.append('templateName', data.template.name);
        formData.append('templateSize', data.template.size);
        formData.append('templateFilename', data.template.filename);
      }

      const response = await api.post("/admin/updateScope3Template", formData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.status == 200) {
        dispatch(showNotification({ 'message': response.data.message, type: 'success' }));
      } else {
        dispatch(showNotification({
          message: 'Please complete all fields',
          type: 'error'
        }));
      }
      getTemplates();
      setIsModalOpen(false);
      reset();
      setEditingTemplate(null);
    } catch (error) {
      dispatch(showNotification({ 'message': error.message, type: 'error' }));
      console.error('Error submitting template:', error);
    }
  };

  const handleEdit = (template) => {
    const templateFor = template.amount_based
      ? 'amount_based'
      : template.quantity_based
        ? 'quantity_based'
        : 'other';

    reset({
      sourceId: template.cat_id,
      template: {
        name: template.template_filename,
        path: template.template_filepath,
        filename: template.template_filename,
        size: template.template_size || 0
      },
      startDate: new Date(template.apply_from),
      endDate: new Date(template.apply_to),
      templateFor: templateFor
    });

    setSelectedData(template);
    setIsModalOpen(true);

    setEditingTemplate({
      template_filename: template.template_filename,
      template_filepath: template.template_filepath,
      template_size: template.template_size
    });
  };

  const handleRemove = async (templateId) => {
    try {
      const response = await api.patch(`/admin/deleteScope3Template/${templateId}`);
      getTemplates();
      if (response.data.status == 200) {
        dispatch(showNotification({ message: response.data.message, type: 'success' }))
      } else {
        dispatch(showNotification({ message: response.data.message, type: 'error' }))
      }
    } catch (error) {
      dispatch(showNotification({ message: error.message, type: 'error' }))
      console.error('Error removing template:', error);
    }
  };

  const handleToggleActive = async (templateId, currentStatus) => {
    try {
      const response = await api.patch(`/admin/toggleScope3Template/${templateId}`, {
        is_active: !currentStatus
      });
      if (response.data.status == 200) {
        dispatch(showNotification({ message: `Template ${!currentStatus ? 'activated' : 'deactivated'} successfully`, type: 'success' }))
      } else {
        dispatch(showNotification({
          message: `Template ${!currentStatus ? 'activated' : 'deactivated'} successfully`,
          type: 'error' // You might want to change this to 'success' based on your use case
        }));

      }
      getTemplates();
    } catch (error) {
      dispatch(showNotification({ 'message': error.message, type: 'error' }));
      console.error('Error toggling template status:', error);
    }
  };

  const forceDownload = (url, filename) => {
    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', filename || 'template.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(error => console.error("Error forcing download:", error));
  };


  const handleDownload = async (templateId, filename) => {
    try {
      const response = await api.get(`/admin/downloadScope3Template/${templateId}`);
      if (response.data.fileUrl) {
        forceDownload(response.data.fileUrl, filename);

        dispatch(showNotification({ message: 'Download successful!', type: 'success' }));
      } else {
        throw new Error("File URL not found in response");
      }
      // const url = window.URL.createObjectURL(new Blob([response.data]));
      // const link = document.createElement('a');
      // link.href = url;
      // link.setAttribute('download', filename || 'template.xlsx');
      // document.body.appendChild(link);
      // link.click();
      // link.remove();

      // dispatch(showNotification({message: 'Download successful!',type: 'success'}));
    } catch (error) {
      dispatch(showNotification({ 'message': error.message, 'type': 'error' }))
      console.error('Error downloading template:', error);
    }
  };


  return (
    <>
      <PageHeader title="Scope3 Templates">
        <Box sx={{ width: '100%', mb: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search templates..."
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => debouncedSearch(e.target.value)}
              className="min-w-[300px]"
            />

            {/* Status Filter */}
           <FormControl size="small" className="min-w-[150px]">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => {
                setPage(0);
                setFilters(prev => ({ ...prev, status: e.target.value }))
              }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" className="min-w-[150px]">
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.type}
              label="Type"
              onChange={(e) => {
                setPage(0);
                setFilters(prev => ({ ...prev, type: e.target.value }))
              }}
            >
              <MenuItem value="ALL">All Types</MenuItem>
              <MenuItem value="AMOUNT_BASED">Amount Based</MenuItem>
              <MenuItem value="QUANTITY_BASED">Quantity Based</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>

            {/* Sort By */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortBy: e.target.value }))
                }}
              >
                <MenuItem value="created_at">Created Date</MenuItem>
                <MenuItem value="template_filename">Template Name</MenuItem>
                <MenuItem value="apply_from">Applicable From</MenuItem>
                <MenuItem value="apply_to">Applicable To</MenuItem>
              </Select>
            </FormControl>

            {/* Sort Order */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sortOrder}
                label="Order"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortOrder: e.target.value }))
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </div>
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text="Add Template"
            click={() => {
              reset();
              setEditingTemplate(null);
              setIsModalOpen(true);
            }}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Template For</TableCell>
                <TableCell>Effective Start Date</TableCell>
                <TableCell>Effective End Date</TableCell>
                <TableCell>Template Available</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{sources.find(s => s.id === template.cat_id)?.cat_title}</TableCell>
                  <TableCell>{template.amount_based ? "Amount Based" : template.quantity_based ? "Quantity Based" : "Other"}</TableCell>
                  <TableCell>{new Date(template.apply_from).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(template.apply_to).toLocaleDateString()}</TableCell>
                  <TableCell>{template.is_active ? "Yes" : "No"}</TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Edit Button */}
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(template)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <EditIcon size={20} />
                    </IconButton>

                    {/* Download Button */}
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(template.id, template.template_filename)}
                      sx={{
                        backgroundColor: '#3d85c6',
                        color: 'white',
                        '&:hover': { backgroundColor: '#39a1ff' },
                        p: '8px',
                      }}
                    >
                      <DownloadIcon size={20} />
                    </IconButton>

                    {/* Active Inactive Button */}
                    <IconButton
                      size="small"
                      onClick={() => handleToggleActive(template.id, template.is_active)}
                      sx={{
                        backgroundColor: template.is_active ? '#df3e3e' : '#2bad2f',
                        color: 'white',
                        '&:hover': { backgroundColor: template.is_active ? '#ff0000' : '#22e629' },
                        p: '8px',
                        cursor: 'pointer',
                        opacity: template.is_active ? 1 : 0.6,
                      }}
                    >
                      <Power size={20} />
                    </IconButton>


                    {/* Delete Button */}
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(template.id)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={7}
                  count={totalScope3Templates}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) => 
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Dialog
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {editingTemplate ? "Edit Template" : "Add New Template"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => setIsModalOpen(false)}
            />
          </div>


          <DialogContent>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} >
              <Controller
                control={control}
                name="sourceId"
                sx
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.sourceId} sx={{ mb: 2 }} size="small">
                    <InputLabel>Select Scope3 Source<span style={{ color: 'red' }}>*</span></InputLabel>
                    <Select
                      {...field}
                      label="Select Scope3 Source"
                    >
                      {sources.map(source => (
                        <MenuItem key={source.id} value={source.id}>
                          {source.cat_title}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.sourceId && (
                      <FormHelperText>{errors.sourceId.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
              <div className="flex gap-2 mb-5">
                <div className="flex flex-col w-full">
                  <label className="text-sm font-medium text-gray-700 mb-2">
                    Start Date<span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl fullWidth error={!!error}>
                        <DatePicker
                          selected={value}
                          onChange={onChange}
                          dateFormat="MMMM yyyy"
                          showMonthYearPicker
                          showFullMonthYearPicker
                          placeholderText="Select Start Date"
                          className="date-picker-input"
                          calendarClassName="year-month-picker"
                          portalId="root"
                          openToDate={value || new Date()}
                        />
                        {error && <span className="text-red-500 text-sm">{error.message}</span>}
                      </FormControl>
                    )}
                  />
                </div>

                <div className="flex flex-col w-full">
                  <label className="text-sm font-medium text-gray-700 mb-2">
                    End Date<span className="text-red-500">*</span>
                  </label>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <FormControl fullWidth error={!!error}>
                        <DatePicker
                          selected={value}
                          onChange={onChange}
                          dateFormat="MMMM yyyy"
                          showMonthYearPicker
                          showFullMonthYearPicker
                          placeholderText="Select End Date"
                          className="date-picker-input"
                          calendarClassName="year-month-picker"
                          portalId="root"
                          openToDate={value || new Date()}
                          disabled={!watch('startDate')}
                          minDate={watch('startDate')}
                          filterDate={(date) => {
                            const startDate = watch('startDate');
                            if (!startDate) return true;
                            return !(date.getFullYear() === startDate.getFullYear() &&
                              date.getMonth() === startDate.getMonth());
                          }}
                        />
                        {error && <span className="text-red-500 text-sm">{error.message}</span>}
                      </FormControl>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-col w-full mb-5">
                <Controller
                  control={control}
                  name="templateFor"
                  sx
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.sourceId} sx={{ mb: 3 }} size="small">
                      <InputLabel>Template For<span style={{ color: 'red' }}>*</span></InputLabel>
                      <Select
                        {...field}
                        label="Template For"
                      >
                        <MenuItem value={'amount_based'}>Amount Based</MenuItem>
                        <MenuItem value={'quantity_based'}>Quantity Based</MenuItem>
                        <MenuItem value={'other'}>Other</MenuItem>
                      </Select>
                      {errors.templateFor && (
                        <FormHelperText>{errors.templateFor.message}</FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </div>

              <Controller
                control={control}
                name="template"
                render={({ field }) => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    <CloudinaryFileUploader
                      multiple={false}
                      allowedTypes="application/pdf,xlsx"
                      onUploadComplete={(fileResponse) => {
                        setValue('template', fileResponse);
                      }}
                      existingFiles={editingTemplate ? [{
                        name: editingTemplate.template_filename || '',
                        path: editingTemplate.template_filepath || '',
                        filename: editingTemplate.template_filename || '',
                        size: editingTemplate.template_size || 0
                      }] : []}
                    />
                    {errors.template && (
                      <FormHelperText error>{errors.template.message}</FormHelperText>
                    )}
                  </Box>
                )}
              />

              <div className="flex justify-end gap-5 items-center mt-5">
                <CustomButton
                  className="bg-gray-500 hover:bg-gray-700 text-white "
                  click={() => setIsModalOpen(false)}
                  text="Cancel"
                />
                <CustomButton
                  className="bg-green-600 hover:bg-green-700 text-white"
                  text="Submit"
                  type='submit'
                />
              </div>
            </Box>
          </DialogContent>
        </Dialog>
      </PageHeader>
    </>
  );
};

export default Scope3TemplateMaster;